import * as React from "react";
import useEmblaCarousel, { type UseEmblaCarouselType } from "embla-carousel-react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "@core/utils/domUtil";
import { Button } from "@core/components/ui/Button";

type CarouselApi = UseEmblaCarouselType[1];
type UseCarouselParameters = Parameters<typeof useEmblaCarousel>;
type CarouselOptions = UseCarouselParameters[0];
type CarouselPlugin = UseCarouselParameters[1];

type CarouselProps = {
  opts?: CarouselOptions;
  plugins?: CarouselPlugin;
  orientation?: "horizontal" | "vertical";
  setApi?: (api: CarouselApi) => void;
};

type CarouselContextProps = {
  carouselRef: ReturnType<typeof useEmblaCarousel>[0];
  api: ReturnType<typeof useEmblaCarousel>[1];
  scrollTo: (index: number) => void;
  scrollPrev: () => void;
  scrollNext: () => void;
  canScrollPrev: boolean;
  canScrollNext: boolean;
  slideCount: number;
  selectedIndex: number;
} & CarouselProps;

const CarouselContext = React.createContext<CarouselContextProps | null>(null);

function useCarousel() {
  const context = React.useContext(CarouselContext);

  if (!context) {
    throw new Error("useCarousel must be used within a <Carousel />");
  }

  return context;
}

function Carousel({ orientation = "horizontal", opts, setApi, plugins, className, children, ...props }: React.ComponentProps<"div"> & CarouselProps) {
  const [carouselRef, api] = useEmblaCarousel(
    {
      ...opts,
      axis: orientation === "horizontal" ? "x" : "y",
    },
    plugins,
  );
  const [canScrollPrev, setCanScrollPrev] = React.useState(false);
  const [canScrollNext, setCanScrollNext] = React.useState(false);
  const [slideCount, setSlideCount] = React.useState(0);
  const [selectedIndex, setSelectedIndex] = React.useState(0);

  const onSelect = React.useCallback((api: CarouselApi) => {
    if (!api) return;
    setCanScrollPrev(api.canScrollPrev());
    setCanScrollNext(api.canScrollNext());
    setSelectedIndex(api.selectedScrollSnap());
  }, []);

  const scrollPrev = React.useCallback(() => {
    api?.scrollPrev();
  }, [api]);

  const scrollNext = React.useCallback(() => {
    api?.scrollNext();
  }, [api]);

  const scrollTo = React.useCallback(
    (index: number) => {
      api?.scrollTo(index);
    },
    [api],
  );

  const handleKeyDown = React.useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (event.key === "ChevronLeft") {
        event.preventDefault();
        scrollPrev();
      } else if (event.key === "ChevronRight") {
        event.preventDefault();
        scrollNext();
      }
    },
    [scrollPrev, scrollNext],
  );

  React.useEffect(() => {
    if (!api || !setApi) return;
    setApi(api);
  }, [api, setApi]);

  React.useEffect(() => {
    if (!api) return;
    onSelect(api);

    api.on("reInit", onSelect);
    api.on("select", onSelect);

    const updateSlideCount = () => {
      setSlideCount(api.slideNodes().length);
    };

    updateSlideCount();
    api.on("reInit", updateSlideCount);

    return () => {
      api?.off("select", onSelect);
    };
  }, [api, onSelect]);

  return (
    <CarouselContext.Provider
      value={{
        carouselRef,
        api: api,
        opts,
        orientation: orientation || (opts?.axis === "y" ? "vertical" : "horizontal"),
        scrollPrev,
        scrollNext,
        scrollTo,
        canScrollPrev,
        canScrollNext,
        slideCount,
        selectedIndex
      }}
    >
      <div onKeyDownCapture={handleKeyDown} className={cn("relative", className)} role="region" aria-roledescription="carousel" data-slot="carousel" {...props}>
        {children}
      </div>
    </CarouselContext.Provider>
  );
}

function CarouselContent({ className, ...props }: React.ComponentProps<"div">) {
  const { carouselRef, orientation } = useCarousel();

  return (
    <div ref={carouselRef} className="overflow-hidden" data-slot="carousel-content">
      <div className={cn("flex", orientation === "horizontal" ? "-ml-4" : "-mt-4 flex-col", className)} {...props} />
    </div>
  );
}

function CarouselItem({ className, ...props }: React.ComponentProps<"div">) {
  const { orientation } = useCarousel();

  return (
    <div
      role="group"
      aria-roledescription="slide"
      data-slot="carousel-item"
      className={cn("min-w-0 shrink-0 grow-0 basis-full", orientation === "horizontal" ? "pl-4" : "pt-4", className)}
      {...props}
    />
  );
}

function CarouselPrevious({ className, variant = "link", size = "icon", ...props }: React.ComponentProps<typeof Button>) {
  const { orientation, scrollPrev, canScrollPrev } = useCarousel();

  return (
    <Button
      data-slot="carousel-previous"
      variant={variant}
      size={size}
      className={cn("absolute size-8 cursor-pointer", orientation === "horizontal" ? "top-1/2 -left-12 -translate-y-1/2" : "-top-12 left-1/2 -translate-x-1/2 rotate-90", className)}
      disabled={!canScrollPrev}
      onClick={scrollPrev}
      {...props}
    >
      <ChevronLeft strokeWidth={4} />

      <span className="sr-only">Previous slide</span>
    </Button>
  );
}

function CarouselNext({ className, variant = "link", size = "icon", ...props }: React.ComponentProps<typeof Button>) {
  const { orientation, scrollNext, canScrollNext } = useCarousel();

  return (
    <Button
      data-slot="carousel-next"
      variant={variant}
      size={size}
      className={cn("absolute size-8 cursor-pointer", orientation === "horizontal" ? "top-1/2 -right-12 -translate-y-1/2" : "-bottom-12 left-1/2 -translate-x-1/2 rotate-90", className)}
      disabled={!canScrollNext}
      onClick={scrollNext}
      {...props}
    >
      <ChevronRight strokeWidth={4}/>
      <span className="sr-only">Next slide</span>
    </Button>
  );
}

function CarouselTo({ index, className, size = "icon", ...props }: { index: number } & React.ComponentProps<typeof Button>) {
  const { scrollTo, selectedIndex } = useCarousel();

  return (
    <Button
      data-slot="carousel-to"
      size={size}
      className={cn(
        "m-[3px] rounded-full transition-none hover:bg-white",
        selectedIndex === index
          ? "bg-white w-[42px] h-[5px] lg:h-[8px]"
          : "bg-white/60 w-[5px] h-[5px] lg:w-[8px] lg:h-[8px] hover:bg-white/60",
        className,
      )}
      onClick={() => scrollTo(index)}
      aria-current={selectedIndex === index ? "true" : undefined}
      {...props}
    >
      <span className="sr-only">Go to slide {index + 1}</span>
    </Button>
  );
}

function CarouselControls({ useArrow, useIndicator }: { useArrow?: boolean, useIndicator?: boolean }) {
  const { slideCount } = useCarousel();
  return (
    <div className={cn(
      "bottom-[14px] left-[20px] flex absolute",
      "lg:bottom-[30px] lg:left-[40px] "
    )}>
      {useIndicator && (
        <div className={cn("rounded-full p-[5px] inline-flex items-center bg-black/20")}>
          {Array.from({ length: slideCount }).map((_, i) => (
            <CarouselTo key={i} index={i} />
          ))}
        </div>
      )}

      {useArrow && (
        <div className={cn("inline-flex items-center rounded-full ml-[6px] bg-black/20 px-[4px]")}>
          <CarouselPrevious className={"static translate-none text-white/70 hover:text-white w-[24px] h-[24px]"} />
          <CarouselNext className={"static translate-none text-white/70 hover:text-white w-[24px] h-[24px]"}/>
        </div>
      )}
    </div>
  );
}

export {
  type CarouselApi,
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselPrevious,
  CarouselNext,
  CarouselTo,
  CarouselControls,
};
